import type { InputState } from './Physics';
import { keyboardDriveMode } from '../ui/KeyboardDriveMode';
import { FIXED_UPDATE_DT } from './FrameTiming';

/** A touch source produces an InputState snapshot per frame. Both the
 *  4-button TouchInput and the twin-stick StickInput satisfy this. */
export interface TouchInputSource {
  getRaw(): Readonly<InputState>;
  reset(): void;
}

const KEY_THROTTLE = ['KeyW', 'ArrowUp'];
const KEY_BRAKE = ['KeyS', 'ArrowDown', 'Space'];
const KEY_LEFT = ['KeyA', 'ArrowLeft'];
const KEY_RIGHT = ['KeyD', 'ArrowRight'];
// Differential-drive keyboard mode: hold Q/E to drive each engine
// directly (twin-stick keyboard equivalent). When either is held the
// game drops out of legacy WASD into the per-engine physics path.
// Space within this mode acts as an e-brake — reversing the OPPOSITE
// engine for a hard drift, or both engines if both Q/E are held.
const KEY_LEFT_ENGINE = ['KeyQ'];
const KEY_RIGHT_ENGINE = ['KeyE'];
const KEY_BOOST = ['Enter'];
const KEY_PAUSE = ['Escape', 'KeyP'];
const KEY_CAMERA = ['KeyC', 'KeyV'];
const KEY_RESET = 'KeyR';

export interface InputManagerOptions {
  onReset?: () => void;
  onPauseToggle?: () => void;
  onCameraToggle?: () => void;
  onAnyStart?: () => void;
  steerRiseRate?: number;
  steerReturnRate?: number;
  pedalRiseRate?: number;
  pedalReturnRate?: number;
}

export class InputManager {
  private readonly keys = new Set<string>();
  private readonly opts: InputManagerOptions;
  private readonly state: InputState = { throttle: 0, brake: 0, steer: 0, boost: 0 };
  private readonly raw: InputState = { throttle: 0, brake: 0, steer: 0, boost: 0 };
  private steerProgress = 0;
  private touch: TouchInputSource | null = null;
  steerRiseRate: number;
  steerReturnRate: number;
  pedalRiseRate: number;
  pedalReturnRate: number;

  constructor(opts: InputManagerOptions = {}) {
    this.opts = opts;
    this.steerRiseRate = opts.steerRiseRate ?? 3.0;
    this.steerReturnRate = opts.steerReturnRate ?? 10.0;
    this.pedalRiseRate = opts.pedalRiseRate ?? 10.0;
    this.pedalReturnRate = opts.pedalReturnRate ?? 14.0;
  }

  setTouch(touch: TouchInputSource | null): void {
    // If a button/stick is held at the moment the player swaps modes,
    // the outgoing source still has a non-zero snapshot. Force-release
    // it so its last frame doesn't leak into the next source's state.
    if (this.touch && this.touch !== touch) {
      this.touch.reset();
    }
    this.touch = touch;
    // Drop legacy and per-engine axes; the next pollTouch() of the new
    // source will repopulate them from a clean baseline.
    this.raw.throttle = 0;
    this.raw.brake = 0;
    this.raw.steer = 0;
    this.raw.leftThrust = undefined;
    this.raw.rightThrust = undefined;
    this.state.leftThrust = undefined;
    this.state.rightThrust = undefined;
  }

  attach(): void {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  getState(): Readonly<InputState> {
    return this.state;
  }

  reset(): void {
    this.clearAllInputs();
  }

  /** Move digital inputs like virtual analog axes. Called once per physics step. */
  update(dt = FIXED_UPDATE_DT): void {
    this.recomputeRaw();
    this.pollGamepad();
    this.pollTouch();
    this.state.throttle = __test__stepAxis(
      this.state.throttle,
      this.raw.throttle,
      this.raw.throttle > this.state.throttle ? this.pedalRiseRate : this.pedalReturnRate,
      dt,
    );
    this.state.brake = __test__stepAxis(
      this.state.brake,
      this.raw.brake,
      this.raw.brake > this.state.brake ? this.pedalRiseRate : this.pedalReturnRate,
      dt,
    );
    this.state.steer = this.stepSteering(dt);
    // Per-engine thrust passes through unsmoothed: the source is already
    // analog (a finger on a stick), and the ramp would only blur the
    // player's intent. Either both undefined (legacy mode) or both
    // numbers (stick mode); never one without the other.
    this.state.leftThrust = this.raw.leftThrust;
    this.state.rightThrust = this.raw.rightThrust;
    // Boost is a digital trigger — pass through directly so the press
    // edge is sharp (no pedal ramp).
    this.state.boost = this.raw.boost;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (__test__shouldIgnoreKeyboardTarget(e.target)) return;
    if (e.repeat) return;
    if (__test__isHandledGameplayKey(e.code)) e.preventDefault();
    this.keys.add(e.code);
    const isPauseKey = __test__isPauseKey(e.code);
    const isCameraKey = __test__isCameraKey(e.code);
    if (!isPauseKey && !isCameraKey) this.opts.onAnyStart?.();
    if (e.code === KEY_RESET) this.opts.onReset?.();
    if (isPauseKey) {
      this.opts.onPauseToggle?.();
    }
    if (isCameraKey) this.opts.onCameraToggle?.();
    this.recomputeRaw();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (__test__shouldIgnoreKeyboardTarget(e.target)) return;
    if (__test__isHandledGameplayKey(e.code)) e.preventDefault();
    this.keys.delete(e.code);
    this.recomputeRaw();
  };

  private onBlur = (): void => {
    this.clearAllInputs();
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.clearAllInputs();
  };

  private clearAllInputs(): void {
    this.keys.clear();
    this.steerProgress = 0;
    this.raw.throttle = 0;
    this.raw.brake = 0;
    this.raw.steer = 0;
    this.raw.boost = 0;
    this.raw.leftThrust = undefined;
    this.raw.rightThrust = undefined;
    this.state.throttle = 0;
    this.state.brake = 0;
    this.state.steer = 0;
    this.state.boost = 0;
    this.state.leftThrust = undefined;
    this.state.rightThrust = undefined;
    this.touch?.reset();
    this.recomputeRaw();
  }

  private recomputeRaw(): void {
    const raw = __test__resolveRawInput(this.keys, keyboardDriveMode.get() === 'differential');
    this.raw.throttle = raw.throttle;
    this.raw.brake = raw.brake;
    this.raw.steer = raw.steer;
    this.raw.boost = raw.boost;
    // Differential-drive keys produce per-engine values; legacy WASD
    // resolves to undefined so Physics keeps using the legacy path.
    this.raw.leftThrust = raw.leftThrust;
    this.raw.rightThrust = raw.rightThrust;
  }

  private pollTouch(): void {
    if (!this.touch) return;
    const t = this.touch.getRaw();
    if (t.throttle > this.raw.throttle) this.raw.throttle = t.throttle;
    if (t.brake > this.raw.brake) this.raw.brake = t.brake;
    if (t.steer !== 0) {
      this.raw.steer = Math.abs(t.steer) > Math.abs(this.raw.steer) ? t.steer : this.raw.steer;
    }
    if ((t.boost ?? 0) > (this.raw.boost ?? 0)) this.raw.boost = t.boost;
    // Per-engine thrust: when the touch source provides it, it owns the
    // racer this frame. Pin keyboard-derived legacy axes to 0 so they
    // don't double-drive through the buttons-mode path inside Physics.
    if (t.leftThrust !== undefined || t.rightThrust !== undefined) {
      this.raw.leftThrust = t.leftThrust ?? 0;
      this.raw.rightThrust = t.rightThrust ?? 0;
      this.raw.throttle = 0;
      this.raw.brake = 0;
      this.raw.steer = 0;
    }
  }

  private pollGamepad(): void {
    const pads = globalThis.navigator?.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad) continue;
      const rt = pad.buttons[7]?.value ?? 0;
      const lt = pad.buttons[6]?.value ?? 0;
      const lx = pad.axes[0] ?? 0;
      if (rt > 0.05 || lt > 0.05 || Math.abs(lx) > 0.1) {
        this.raw.throttle = Math.max(this.raw.throttle, rt);
        this.raw.brake = Math.max(this.raw.brake, lt);
        if (Math.abs(lx) > 0.1) {
          this.raw.steer = Math.abs(lx) > Math.abs(this.raw.steer) ? lx : this.raw.steer;
        }
      }
    }
  }

  private stepSteering(dt: number): number {
    const target = this.raw.steer;
    const targetSign = Math.sign(target);
    if (targetSign === 0) {
      this.steerProgress = 0;
      return __test__stepAxis(this.state.steer, 0, this.steerReturnRate, dt);
    }

    const currentSign = Math.sign(this.state.steer);
    if (currentSign !== 0 && currentSign !== targetSign) {
      this.steerProgress = 0;
    }

    this.steerProgress = Math.min(1, this.steerProgress + this.steerRiseRate * dt);
    return targetSign * __test__progressiveSteer(this.steerProgress) * Math.abs(target);
  }
}

export function __test__isBrakeKey(code: string): boolean {
  return KEY_BRAKE.includes(code);
}

export function __test__isThrottleKey(code: string): boolean {
  return KEY_THROTTLE.includes(code);
}

export function __test__isSteerKey(code: string): boolean {
  return KEY_LEFT.includes(code) || KEY_RIGHT.includes(code);
}

export function __test__isPauseKey(code: string): boolean {
  return KEY_PAUSE.includes(code);
}

export function __test__isCameraKey(code: string): boolean {
  return KEY_CAMERA.includes(code);
}

export function __test__isEngineKey(code: string): boolean {
  return KEY_LEFT_ENGINE.includes(code) || KEY_RIGHT_ENGINE.includes(code);
}

export function __test__isBoostKey(code: string): boolean {
  return KEY_BOOST.includes(code);
}

export function __test__isHandledGameplayKey(code: string): boolean {
  return __test__isThrottleKey(code)
    || __test__isBrakeKey(code)
    || __test__isSteerKey(code)
    || __test__isEngineKey(code)
    || __test__isBoostKey(code)
    || __test__isPauseKey(code)
    || __test__isCameraKey(code)
    || code === KEY_RESET;
}

export function __test__shouldIgnoreKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return target.isContentEditable;
}

export function __test__resolveRawInput(
  keys: ReadonlySet<string>,
  differentialEnabled = true,
): InputState {
  // Differential keyboard mode: any Q/E held drops into the per-engine
  // physics path. Space-as-brake within this mode becomes the e-brake,
  // reversing the engine you're not commanding (or both for a symmetric
  // brake) — that's what gives the "pull into a turn" feel. Gated on
  // the user's keyboard-drive-mode setting so a stray Q/E press doesn't
  // surprise WASD-only players.
  const boost = KEY_BOOST.some((c) => keys.has(c)) ? 1 : 0;
  const lEng = differentialEnabled && KEY_LEFT_ENGINE.some((c) => keys.has(c));
  const rEng = differentialEnabled && KEY_RIGHT_ENGINE.some((c) => keys.has(c));
  if (lEng || rEng) {
    const airbrake = KEY_BRAKE.some((c) => keys.has(c));
    let leftThrust = lEng ? 1 : 0;
    let rightThrust = rEng ? 1 : 0;
    if (airbrake) {
      if (lEng && rEng) {
        leftThrust = -1;
        rightThrust = -1;
      } else if (lEng) {
        rightThrust = -1;
      } else {
        leftThrust = -1;
      }
    }
    return { throttle: 0, brake: 0, steer: 0, leftThrust, rightThrust, boost };
  }

  const throttle = KEY_THROTTLE.some((c) => keys.has(c)) ? 1 : 0;
  const brake = KEY_BRAKE.some((c) => keys.has(c)) ? 1 : 0;
  const left = KEY_LEFT.some((c) => keys.has(c)) ? 1 : 0;
  const right = KEY_RIGHT.some((c) => keys.has(c)) ? 1 : 0;
  return { throttle, brake, steer: right - left, boost };
}

export function __test__stepAxis(current: number, target: number, unitsPerSecond: number, dt: number): number {
  const maxDelta = Math.max(0, unitsPerSecond * dt);
  const delta = target - current;
  if (Math.abs(delta) <= maxDelta) return target;
  return current + Math.sign(delta) * maxDelta;
}

export function __test__progressiveSteer(progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return 0.5 - Math.cos(p * Math.PI) * 0.5;
}
